import type { SessionData } from "owa-service/lib/types/SessionData";
import { getPerformanceNow } from "owa-performance";
import { hasStashedSlowNetworkSessionData } from "./slowNetworkSessionData";

interface CalendarSettingsComparison {
    hasCachedSessionData: boolean;
    hasServiceSessionData: boolean;
    cachedViewType?: number;
    serviceViewType?: number;
    cachedWeekStartDay?: number;
    serviceWeekStartDay?: number;
    cachedSelectedCalendarsCount?: number;
    serviceSelectedCalendarsCount?: number;
    isSelectedCalendarsMismatch?: boolean;
}

interface ModuleSwitchCalendarSettingsComparison extends CalendarSettingsComparison {
    startupModule: string;
    isNetworkSessionDataHeldAtCalendarInit?: boolean;
    msFromNetworkSessionDataToCalendarInit?: number;
}

// What app startup keeps from the race between cached and network session data until Calendar first
// initializes from another module. That can be long after both copies are gone.
interface StartupRaceState {
    settingsComparison: CalendarSettingsComparison;
    startupModule: string;
    // Monotonic timestamp set only when the network copy arrived after the cached copy won.
    networkSessionDataArrivalTime?: number;
}

// Only one per page: the boot race runs once, and a failed boot retries by reloading the page.
let raceState: StartupRaceState | undefined;
// Set once Calendar takes the comparison, so a network copy arriving later can't record another.
let isComparisonTaken = false;

// The roamed Calendar settings in each copy; a missing copy leaves its fields undefined.
export function getCalendarSettingsComparison(cachedSessionData: SessionData | undefined, serviceSessionData: SessionData | undefined): CalendarSettingsComparison {
    const cachedViewStateConfiguration = cachedSessionData?.owaUserConfig?.ViewStateConfiguration;
    const serviceViewStateConfiguration = serviceSessionData?.owaUserConfig?.ViewStateConfiguration;
    const cachedUserOptions = cachedSessionData?.owaUserConfig?.UserOptions;
    const serviceUserOptions = serviceSessionData?.owaUserConfig?.UserOptions;
    
    const cachedSelectedCalendars = cachedViewStateConfiguration?.SelectedCalendarsDesktop;
    const serviceSelectedCalendars = serviceViewStateConfiguration?.SelectedCalendarsDesktop;
    
    return {
        hasCachedSessionData: !!cachedSessionData,
        hasServiceSessionData: !!serviceSessionData,
        cachedViewType: cachedViewStateConfiguration?.CalendarViewTypeDesktop,
        serviceViewType: serviceViewStateConfiguration?.CalendarViewTypeDesktop,
        cachedWeekStartDay: cachedUserOptions?.WeekStartDay,
        serviceWeekStartDay: serviceUserOptions?.WeekStartDay,
        // Calendar ids identify shared and delegate mailboxes, so only counts are logged.
        cachedSelectedCalendarsCount: getDistinctCount(cachedSelectedCalendars),
        serviceSelectedCalendarsCount: getDistinctCount(serviceSelectedCalendars),
        // Not derivable from the counts above, because the two copies can hold the same number of
        // calendars while disagreeing on which ones.
        isSelectedCalendarsMismatch: getIsSelectedCalendarsMismatch(cachedSelectedCalendars, serviceSelectedCalendars),
    };
}

// Called when the boot race resolves, with the copy that won.
export function recordSessionDataRaceResult(winningSessionData: SessionData, winner: "offline" | "network", startupModule: string): void {
    // Keeps the late network copy if it was recorded first.
    if (raceState || isComparisonTaken) {
        return;
    }
    
    try {
        // Only the winner is known here. If the cached copy won, recordLateNetworkSessionData adds
        // the network copy when it arrives. If the network won, its copy initialized the session.
        // The cached copy is not needed for the cache-win analysis.
        const cachedSessionData = winner === "offline" ? winningSessionData : undefined;
        const serviceSessionData = winner === "network" ? winningSessionData : undefined;
        raceState = {
            settingsComparison: getCalendarSettingsComparison(cachedSessionData, serviceSessionData),
            startupModule,
        };
    }
    catch {
        // Runs on the boot path, which telemetry must not fail.
    }
}

// Called when the network copy arrives after the cached copy won.
// Replaces the cached-only comparison with one of both copies.
export function recordLateNetworkSessionData(cachedSessionData: SessionData, networkSessionData: SessionData, startupModule: string): void {
    if (isComparisonTaken) {
        return;
    }
    
    try {
        raceState = {
            settingsComparison: getCalendarSettingsComparison(cachedSessionData, networkSessionData),
            startupModule,
            networkSessionDataArrivalTime: getPerformanceNow(),
        };
    }
    catch {
        // Runs on the boot path, which telemetry must not fail.
    }
}

// Direct Calendar boots use the boot event.
// Clear the module-switch snapshot and ignore subsequent race updates on this page.
export function discardModuleSwitchCalendarSettingsComparison(): void {
    raceState = undefined;
    isComparisonTaken = true;
}

// Called when Calendar first initializes from another module.
// Returns the recorded comparison once and undefined when this page recorded no race.
export function takeModuleSwitchCalendarSettingsComparison(): ModuleSwitchCalendarSettingsComparison | undefined {
    const state = raceState;
    raceState = undefined;
    isComparisonTaken = true;
    if (!state) {
        return undefined;
    }
    
    const { settingsComparison, startupModule, networkSessionDataArrivalTime } = state;
    if (networkSessionDataArrivalTime === undefined) {
        return { ...settingsComparison, startupModule };
    }
    
    return {
        ...settingsComparison,
        startupModule,
        // Whether the in-memory 30 s holding slot still had the late copy; IDB isn't checked.
        isNetworkSessionDataHeldAtCalendarInit: hasStashedSlowNetworkSessionData(),
        msFromNetworkSessionDataToCalendarInit: getPerformanceNow() - networkSessionDataArrivalTime,
    };
}

function getDistinctCount(calendarIds: string[] | undefined): number | undefined {
    return calendarIds ? new Set(calendarIds).size : undefined;
}

function getIsSelectedCalendarsMismatch(cachedSelectedCalendars: string[] | undefined, serviceSelectedCalendars: string[] | undefined): boolean | undefined {
    if (!cachedSelectedCalendars || !serviceSelectedCalendars) {
        return undefined;
    }
    
    const cachedIds = new Set(cachedSelectedCalendars);
    const serviceIds = new Set(serviceSelectedCalendars);
    
    return (cachedIds.size !== serviceIds.size ||
        [...cachedIds].some(calendarId => !serviceIds.has(calendarId)));
}
